import type { EditorialCategoryResult } from "@legal-500/legal500-design-system";
import * as api from "./client";

// ============================================================================
// Type Definitions
// ============================================================================

type TaxonomySimpleResponse = {
  editorialCategories: Array<{
    id: number;
    type: string | null;
    displayName: string | null;
    country: string | null;
    practiceGroup: string | null;
    practiceArea: string | null;
  }>;
};

/**
 * Global editorial-category search item. Shape matches the design-system's
 * `EditorialCategoryResult` (plus an extra `practiceAreaId` we ignore) so the
 * `EditorialCategorySelect` picker can consume the search rows directly.
 */
type EditorialCategorySearchItem = {
  id: number;
  practiceAreaId: number | null;
  practiceArea: string | null;
  practiceGroupId: number | null;
  practiceGroup: string | null;
  countryId: number | null;
  country: string | null;
};

type EditorialCategorySearchResponse = {
  editorialCategories: EditorialCategorySearchItem[];
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
  requestId: string;
};

export type EditorialCategory = {
  id: string;
  name: string;
  type?: string;
};

/** Re-exported so consumers of this service can type the picker options. */
export type { EditorialCategoryResult };

// The search endpoint's `limit` is capped at 5000 (see taxonomy-api's
// editorial-categories search schema). The full practice-area taxonomy fits well
// within that, so a single unpaged request returns the entire list.
const PRACTICE_AREA_FETCH_LIMIT = 5000;

// ============================================================================
// Service
// ============================================================================

export const editorialCategories = {
  getAll: async (): Promise<EditorialCategory[]> => {
    const response = await api.get<TaxonomySimpleResponse>(
      "/v1/taxonomy/editorial-categories/simple",
    );
    return response.editorialCategories.map(cat => ({
      id: String(cat.id),
      name: cat.displayName || cat.country || `Category ${cat.id}`,
      type: cat.type || undefined,
    }));
  },

  /**
   * Fetches the FULL global practice-area taxonomy in a single request (Region ›
   * optional Practice Group › Practice Area), scoped to categories linked to a
   * current (Active/Researching) edition but not otherwise scoped by country.
   * The returned `EditorialCategoryResult[]` is fed wholesale to
   * `EditorialCategorySelect`, which does its own search / tree / chip handling
   * client-side.
   */
  fetchAll: async (): Promise<EditorialCategoryResult[]> => {
    const response = await api.get<EditorialCategorySearchResponse>(
      `/v1/taxonomy/editorial-categories/search?limit=${PRACTICE_AREA_FETCH_LIMIT}`,
    );

    return response.editorialCategories.map(item => ({
      id: item.id,
      practiceArea: item.practiceArea,
      practiceGroupId: item.practiceGroupId,
      practiceGroup: item.practiceGroup,
      countryId: item.countryId,
      country: item.country,
    }));
  },
};
